import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export type SheetTab = {
  id: string
  label: string
  content: ReactNode
}

export type BottomSheetDrawerProps = {
  isOpen: boolean
  onClose: () => void
  tabs: SheetTab[]
  initialTabId?: string
  title?: string
  closeLabel?: string
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M1 1L13 13M1 13L13 1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function BottomSheetDrawer({
  isOpen,
  onClose,
  tabs,
  initialTabId,
  title,
  closeLabel,
}: BottomSheetDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)
  const titleId = useId()
  const tabSetId = useId()
  const [activeTab, setActiveTab] = useState<string>(
    initialTabId || (tabs[0] ? tabs[0].id : ''),
  )
  const [modalActive, setModalActive] = useState(isOpen)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (isOpen) setModalActive(true)
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      if (initialTabId && tabs.some((t) => t.id === initialTabId)) {
        setActiveTab(initialTabId)
      } else if (tabs[0] && !tabs.some((t) => t.id === activeTab)) {
        setActiveTab(tabs[0].id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTabId])

  useEffect(() => {
    if (!modalActive) return

    const panel = panelRef.current
    if (!panel) return

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const bodyOverflow = document.body.style.overflow
    const bodyOverscroll = document.body.style.overscrollBehavior
    document.body.style.overflow = 'hidden'
    document.body.style.overscrollBehavior = 'none'

    const siblings = Array.from(panel.parentElement?.children || []).filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement &&
        element !== panel &&
        !element.classList.contains('sheet-backdrop'),
    )
    const siblingState = siblings.map((element) => ({
      element,
      inert: element.inert,
      ariaHidden: element.getAttribute('aria-hidden'),
    }))
    siblings.forEach((element) => {
      element.inert = true
      element.setAttribute('aria-hidden', 'true')
    })

    const focusInitial = window.requestAnimationFrame(() => {
      const initialTarget = panel.querySelector<HTMLElement>(
        '[data-sheet-initial-focus], button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      ;(initialTarget || panel).focus()
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }

      if (event.key !== 'Tab') return
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (element) =>
          element.tabIndex >= 0 && !element.hidden && element.getClientRects().length > 0,
      )

      if (!focusable.length) {
        event.preventDefault()
        panel.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(focusInitial)
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = bodyOverflow
      document.body.style.overscrollBehavior = bodyOverscroll
      siblingState.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert
        if (ariaHidden == null) element.removeAttribute('aria-hidden')
        else element.setAttribute('aria-hidden', ariaHidden)
      })
      previousFocusRef.current?.focus({ preventScroll: true })
      previousFocusRef.current = null
    }
  }, [modalActive])

  useEffect(() => {
    if (!isOpen) return

    const focusActiveTab = window.requestAnimationFrame(() => {
      const panel = panelRef.current
      if (!panel) return
      const selectedTab = panel.querySelector<HTMLElement>(
        '[role="tab"][aria-selected="true"]',
      )
      if (selectedTab) {
        selectedTab.focus()
      } else if (!panel.contains(document.activeElement)) {
        const closeButton = panel.querySelector<HTMLElement>('.sheet-close-btn')
        ;(closeButton || panel).focus()
      }
    })

    return () => window.cancelAnimationFrame(focusActiveTab)
  }, [activeTab, isOpen])

  const activeTabIndex = Math.max(0, tabs.findIndex((tab) => tab.id === activeTab))
  const isChinese = typeof document !== 'undefined' && document.documentElement.lang.startsWith('zh')
  const resolvedCloseLabel = closeLabel || (isChinese ? '關閉' : 'Close')
  const dialogTitle =
    title || tabs[activeTabIndex]?.label || tabs[0]?.label || (isChinese ? '對話框' : 'Dialog')
  const tabId = (index: number) => `${tabSetId}-tab-${index}`
  const panelId = (index: number) => `${tabSetId}-panel-${index}`

  const handleTabKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number | null = null
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = tabs.length - 1
    if (nextIndex == null) return

    event.preventDefault()
    setActiveTab(tabs[nextIndex].id)
    panelRef.current?.querySelector<HTMLElement>(`#${CSS.escape(tabId(nextIndex))}`)?.focus()
  }

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (!isOpen) setModalActive(false)
      }}
    >
      {isOpen && (
        <>
          <motion.div
            className="sheet-backdrop"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            ref={panelRef}
            className="sheet-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            <h2 id={titleId} className="sheet-dialog-title sheet-sr-only">
              {dialogTitle}
            </h2>

            <div className="sheet-handle-bar" aria-hidden="true">
              <div className="sheet-handle" />
            </div>

            <div className="sheet-header">
              {tabs.length > 1 ? (
                <div className="sheet-tabs" role="tablist">
                  {tabs.map((tab, index) => (
                    <button
                      key={tab.id}
                      id={tabId(index)}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      aria-controls={panelId(index)}
                      tabIndex={activeTab === tab.id ? 0 : -1}
                      data-sheet-initial-focus={activeTab === tab.id ? '' : undefined}
                      className={`sheet-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                      onKeyDown={(event) => handleTabKeyDown(event, index)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="sheet-title">{title || tabs[0]?.label}</div>
              )}

              <button
                type="button"
                className="sheet-close-btn"
                onClick={onClose}
                aria-label={resolvedCloseLabel}
                data-sheet-initial-focus={tabs.length <= 1 ? '' : undefined}
              >
                <CloseIcon />
              </button>
            </div>

            {tabs.map((tab, index) => {
              const active = tab.id === activeTab
              return (
                <div
                  key={tab.id}
                  id={panelId(index)}
                  className="sheet-body"
                  role={tabs.length > 1 ? 'tabpanel' : undefined}
                  aria-labelledby={tabs.length > 1 ? tabId(index) : undefined}
                  tabIndex={active ? 0 : -1}
                  hidden={!active}
                >
                  {tab.content}
                </div>
              )
            })}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
